import React, { Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Spinner, initAnalytics, resolveAnalytics } from '@easydent/shared-ui';
import AppCookieBanner from '@/components/AppCookieBanner';

// Patient portal serves unauthenticated flows:
// - /appointments/:token  — cancel / reschedule via link
// - /intake/:token        — pre-appointment intake form
// - /pay/:token           — pay-by-phone payment link
// - /deposit/:token       — booking-deposit payment link
// - /waitlist/:token      — waiting-list freed-slot claim
// - /unsubscribe/:token   — marketing unsubscribe

const AppointmentActionPage = React.lazy(() => import('@/pages/AppointmentActionPage'));
const IntakeFormPage = React.lazy(() => import('@/pages/IntakeFormPage'));
const PaymentPage = React.lazy(() => import('@/pages/PaymentPage'));
const DepositPage = React.lazy(() => import('@/pages/DepositPage'));
const WaitlistClaimPage = React.lazy(() => import('@/pages/WaitlistClaimPage'));
const UnsubscribePage = React.lazy(() => import('@/pages/UnsubscribePage'));
const PortalAcceptPage = React.lazy(() => import('@/pages/PortalAcceptPage'));
const MedicalHistoryFormPage = React.lazy(() => import('@/pages/MedicalHistoryFormPage'));
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage'));
const LoginPage = React.lazy(() => import('@/pages/LoginPage'));
const MagicLoginPage = React.lazy(() => import('@/pages/MagicLoginPage'));
const SetPasswordPage = React.lazy(() => import('@/pages/SetPasswordPage'));
const DashboardPage = React.lazy(() => import('@/pages/DashboardPage'));
const AppointmentsPage = React.lazy(() => import('@/pages/AppointmentsPage'));
const BookPage = React.lazy(() => import('@/pages/BookPage'));
const DemoLoginPage = React.lazy(() => import('@/pages/DemoLoginPage'));
const RecordsPage = React.lazy(() => import('@/pages/RecordsPage'));
const AccountPage = React.lazy(() => import('@/pages/AccountPage'));
const PaymentsPage = React.lazy(() => import('@/pages/PaymentsPage'));

const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <Spinner size="lg" />
  </div>
);

const App: React.FC = () => {
  // Consent-gated analytics. Stays off until this app has a website id AND the visitor consents.
  useEffect(() => {
    initAnalytics(resolveAnalytics('patient'));
  }, []);

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/appointments" element={<AppointmentsPage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/records" element={<RecordsPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/demo" element={<DemoLoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/:token" element={<MagicLoginPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/appointments/:token" element={<AppointmentActionPage />} />
          <Route path="/intake/:token" element={<IntakeFormPage />} />
          <Route path="/pay/:token" element={<PaymentPage />} />
          <Route path="/deposit/:token" element={<DepositPage />} />
          <Route path="/waitlist/:token" element={<WaitlistClaimPage />} />
          <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
          <Route path="/portal/accept/:token" element={<PortalAcceptPage />} />
          <Route path="/intake/history/:token" element={<MedicalHistoryFormPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <AppCookieBanner />
    </>
  );
};

export default App;
